/**
 * Created by jonas on 2022-01-11.
 */
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {gsap} from "gsap";
import {ContinueButton} from "@nolimitcity/slot-launcher/bin/gui/buttons/concretebuttons/ContinueButton";
import {TheCage} from "../TheCage";
import {TheCageSound} from "../sound/TheCageSound";
import {TheCageGameAssets} from "../TheCageGameAssets";
import {TheCageGameMode} from "../server/TheCageGameMode";
import {OutroSign} from "./transition/OutroSign";
import {FreespinsFeatures} from "./parts/FreespinsFeatures";
import {FreespinsOpponentLifeCounterDelegate, FreespinsPlayerLifeCounterDelegate} from "./parts/FreespinsLifeCounter";
import {TheCageFreesSpinReelsView} from "../reels/TheCageFreesSpinReelsView";
import {ScatterSymbol} from "../reels/ScatterSymbol";
import {TheCageSymbolStates} from "../reels/TheCageSymbol";
import {SpineGSAPWrapper} from "../animation/SpineGSAPWrapper";


export interface KillBadgeData {
    name:string,
    attribute:string,
    amount:number,
    round:number
}

export class TheCageTransitionScreenView extends PIXI.Container implements StageLayer {
    protected button:ContinueButton;
    private clickCallback?:Function;
    private background:PIXI.Sprite;
    private topLevelDarkening:PIXI.Sprite;
    private outroSign:OutroSign;
    // intro stuff
    private introText:PIXI.spine.Spine;
    // features shared with FreeSpinReelsView
    public featureContainer:PIXI.Container;
    public features:FreespinsFeatures;
    public opponentCounter: FreespinsOpponentLifeCounterDelegate;
    public playerCounter: FreespinsPlayerLifeCounterDelegate;

    constructor(name?:string) {
        super();
        this.name = name ? name : "TheCageTransitionScreenView";
        this.init();
    }

    protected init() {

        this.background = new PIXI.Sprite(PIXI.Texture.WHITE);
        this.background.tint = PIXI.utils.string2hex("#000000");
        this.background.position.set(360, 360);
        this.background.anchor.set(0.5, 0.5);

        this.topLevelDarkening = new PIXI.Sprite(PIXI.Texture.WHITE);
        this.topLevelDarkening.tint = PIXI.utils.string2hex("#000000");
        this.topLevelDarkening.position.set(360, 360);
        this.topLevelDarkening.anchor.set(0.5, 0.5);
        this.topLevelDarkening.alpha = 0;


        this.button = new ContinueButton();
        this.button.addClickCallback(() => this.onButtonClick());

        this.button.position.set(360, 610);
        this.button.pivot.set(this.button.width * 0.5, this.button.height * 0.5);
        this.button.enable(false);
        this.button.alpha = 0;

        // intro text stuff
        this.introText = new PIXI.spine.Spine(TheCage.assetLoader.getSpineAsset(TheCageGameAssets.FREESPINS_INTRO_ANIMATIONS));
        this.introText.position.set(360, 360);

        // features shared with freespins reel view
        this.featureContainer = new PIXI.Container();
        this.featureContainer.position.set(360,360);
        this.featureContainer.pivot.set(360,360);
        this.featureContainer.scale.set(0.85, 0.85);

        //OUTRO
        this.outroSign = new OutroSign();
        this.outroSign.position.set(360,360);
        this.outroSign.alpha = 0;
        this.outroSign.visible = false;


        this.addChild(this.background);
        //this.addChild(this.introSign);
        this.addChild(this.outroSign);
        this.addChild(this.featureContainer);
        this.addChild(this.introText);
        this.addChild(this.button);
        this.addChild(this.topLevelDarkening);
        this.visible = false;

    }

    onResize():void {
        if (NolimitApplication.isLandscape) {
            this.button.position.set(360, 610);
        } else {
            this.button.position.set(360, NolimitApplication.screenBounds.bottom - 170);
        }

        this.background.width = NolimitApplication.screenBounds.width;
        this.background.height = NolimitApplication.screenBounds.height;
        this.topLevelDarkening.width = NolimitApplication.screenBounds.width;
        this.topLevelDarkening.height = NolimitApplication.screenBounds.height;
    }

    private onButtonClick() {
        this.button.enable(false);
        if (this.clickCallback == undefined) {
            throw new Error("No button click callback");
        }
        this.clickCallback();
        TheCage.soundPlayer.playSound(TheCageSound.CONTINUE_BUTTON);
        gsap.to(this.button, {duration:0.2, alpha:0});
        delete this.clickCallback;
    }

    public enableButton(cb:Function):gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();
        tl.add(() => {
            this.clickCallback = cb;
            this.button.enable(true);
        });
        tl.add(gsap.to(this.button, {duration:0.2, alpha:1}));
        return tl;
    }

    public giveIntroFeaturesTo(target:TheCageFreesSpinReelsView):gsap.core.Timeline {
        const tl = gsap.timeline();
        tl.add(() => {
            target.features = this.features;
            this.featureContainer.removeChild(this.features);
            target.addChild(this.features);

            target.playerCounter = this.playerCounter;
            this.featureContainer.removeChild(this.playerCounter);
            target.addChild(this.playerCounter);

            target.opponentCounter = this.opponentCounter
            this.featureContainer.removeChild(this.opponentCounter);
            target.addChild(this.opponentCounter);
        })
        return tl;
    }

    public showFighterName(level:number):gsap.core.Timeline {
        const tl = gsap.timeline();
        this.introText.alpha = 1;
        tl.add(SpineGSAPWrapper.createTimeline(this.introText, 0, `M${5-level}_intro`));
        tl.add(() => {this.introText.state.setAnimation(0, `M${5-level}_idle`, true)});
        return tl;
    }

    public showFreespinsRewarded(level:number):gsap.core.Timeline {
        const tl = gsap.timeline();
        const adjustedLevel = level == 0 ? 1 : level == 4 ? 4 : 2;
        this.introText.alpha = 1;
        tl.add(SpineGSAPWrapper.createTimeline(this.introText, 1, `blue_intro_${adjustedLevel}`));
        tl.add(() => {this.introText.state.setAnimation(1, `blue_idle_${adjustedLevel}`, true)});
        return tl;
    }

    public showIntro():gsap.core.Timeline {
        this.background.alpha = 0;
        this.outroSign.visible = false;

        const tl:gsap.core.Timeline = gsap.timeline();
        tl.add(() => {
            this.onResize();
            this.visible = true;
        });
        tl.add(gsap.to(this, {duration:0.5}));

        tl.add(()=>{
            TheCage.soundPlayer.playSound(TheCageSound.FS_WIN);
        });
        TheCage.reelView.avalancheMatrixModel.getAllSymbolsWithName("S1").forEach((scatter) => {
          tl.add((<ScatterSymbol>scatter.positionable).getStateAnimation(TheCageSymbolStates.LANDING), '<0.15');
        });

        tl.add(gsap.to(this.topLevelDarkening, {duration:0.5, alpha:1.0, ease:"none"}));
        tl.add(gsap.to(this.background, {duration:0.5, alpha:1.0, ease:"none"}), '<');
        tl.add(TheCage.backgroundElements.hideLogo());
        tl.add(TheCage.reelView.hide(0.1));
        tl.add(TheCage.freeSpinReelView.giveIntroFeaturesTo(this));

        tl.add([
            gsap.to(this.background, {duration:0.5, alpha:0.5, ease:"none"}),
            gsap.to(this.topLevelDarkening, {duration:0.5, alpha:0, ease:"none"}),
            () => {
                TheCage.backgroundElements.setMode(TheCageGameMode.FREESPIN, 1.0);
            },
            TheCage.freeSpinReelView.show(),
            TheCage.backgroundElements.showLogo(),
        ], "<");

        tl.add(()=>{
            if (TheCage.model.data.fsLevel == 3 || TheCage.model.data.fsLevelEnterWith == 4) {
                TheCage.soundPlayer.switchMusic("FS_INTRO_LVL5");
            } else {
                TheCage.soundPlayer.switchMusic("FS_INTRO");
            }
        }, 2.706);
        tl.add(gsap.to(this, {duration: 0.5}));

        return tl;

    }

    public hideIntro():gsap.core.Timeline {
        const tl = gsap.timeline();
        tl.add([
            gsap.to(this.introText, {duration:1.0, alpha:0.0, ease:"none"}),
            gsap.to(this.background, {duration:1.0, alpha:0.0, ease:"none"}),
            TheCage.freeSpinReelView.showForLevelUp(1.0),
            //TheCage.backgroundElements.showLogo(1.0),
        ]);
        tl.add([
            () => {this.introText.state.setEmptyAnimation(0, 0)},
            () => {this.introText.state.setEmptyAnimation(1, 0)},
        ]);
        tl.add(this.giveIntroFeaturesTo(TheCage.freeSpinReelView));

        return tl;
    }

    public showLevelUp(level:number): gsap.core.Timeline {
        this.background.alpha = 0;
        this.outroSign.visible = false;

        const tl:gsap.core.Timeline = gsap.timeline();
        tl.add(() => {
            this.onResize();
            this.visible = true;
        });
        tl.add(gsap.to(this, {duration:0.5}));

        tl.add(TheCage.freeSpinReelView.giveIntroFeaturesTo(this));
        tl.add([
            gsap.to(this.background, {duration:1.0, alpha:0.5, ease:"none"}),
            () => {
                TheCage.backgroundElements.setMode(TheCageGameMode.FREESPIN, 1.0);
            },
            TheCage.freeSpinReelView.hideForLevelUp(1.0),
            //TheCage.backgroundElements.hideLogo(1.0),
        ], "<0.7");


        //tl.add(()=>{
        //    Octagon.soundPlayer.switchMusic("FS_INTRO");
        //}, 1.427);

        return tl;

    }

    // in case we need to do some special stuff when hiding the level up intro
    public hideLevelUp(): gsap.core.Timeline {
        const tl = gsap.timeline();
        tl.add([
            gsap.to(this.introText, {duration:1.0, alpha:0.0, ease:"none"}),
            gsap.to(this.background, {duration:1.0, alpha:0.0, ease:"none"}),
            TheCage.freeSpinReelView.showForLevelUp(1.0),
            //TheCage.backgroundElements.showLogo(1.0),
        ]);
        tl.add([
            () => {this.introText.state.setEmptyAnimation(0, 0)},
            () => {this.introText.state.setEmptyAnimation(1, 0)},
        ]);
        tl.add(this.giveIntroFeaturesTo(TheCage.freeSpinReelView));

        return tl;
    }

    public showMaxWin():gsap.core.Timeline {
        this.background.alpha = 0;
        this.outroSign.alpha = 0;
        this.outroSign.visible = false;

        const tl:gsap.core.Timeline = gsap.timeline();
        tl.add(() => {
            this.onResize();
            this.visible = true;
        });
        //tl.add(gsap.to(this.background, {duration:0.5, alpha:1}));


        return tl;
    }

    public hideMaxWin():gsap.core.Timeline {
        const tl:gsap.core.Timeline = gsap.timeline();
        //tl.add(gsap.to(this.background, {duration:0.5, alpha:0}));
        tl.add(() => {
            this.visible = false;
        });


        return tl;
    }

    public showOutro(amount:number):gsap.core.Timeline {
        this.background.alpha = 0;

        this.outroSign.alpha = 0;
        this.outroSign.hideCountUp();
        this.outroSign.visible = true;

        this.outroSign.setGraphicsLevel(TheCage.model.data.fsLevel);


        const tl:gsap.core.Timeline = gsap.timeline();
        tl.add(() => {
            this.onResize();
            this.visible = true;
        });

        tl.add(() => {
            TheCage.soundPlayer.playSound(TheCageSound.FS_END_SWEEP);
        });
        tl.add(gsap.to(this.topLevelDarkening, {duration:0.5, alpha:1.0, ease:"none"}));
        tl.add(gsap.to(this.background, {duration:0.5, alpha:1.0, ease:"none"}), '<');
        tl.add(TheCage.backgroundElements.hideLogo());
        tl.add(TheCage.freeSpinReelView.hide(0.1));
        tl.add(() => {this.outroSign.alpha = 1;});

        tl.add([
            gsap.to(this.background, {duration:0.5, alpha:0.5, ease:"none"}),
            gsap.to(this.topLevelDarkening, {duration:0.5, alpha:0, ease:"none"}),
            () => {
                TheCage.backgroundElements.setMode(TheCageGameMode.NORMAL, 0.5);
            },
            TheCage.backgroundElements.showLogo(),
        ], "<");
        tl.add(() => {
            TheCage.soundPlayer.fadeOutMusic(150);
            TheCage.soundPlayer.playSound(TheCage.model.data.fsLevel == 4 ? TheCageSound.FS_LVL5_END : TheCageSound.FS_LVL1_TO_4_END);
            TheCage.soundPlayer.startFreespinsOutro();
        },'<');
        if (!TheCage.isWinBelowStake() && TheCage.model.data.accumulatedRoundWin > 0) {
            tl.add(this.outroSign.getCountUp(0, amount), '<0.35');
            tl.add(() => {
                TheCage.soundPlayer.startCountUp();
            }, '<');
        } else {
            tl.add(this.outroSign.getStill(amount), '<0.35');
        }
        tl.add(() => {
            TheCage.soundPlayer.endCountUp(TheCageSound.FS_OUTRO_COUNTUP_END);
            this.outroSign.countUp.stopAt(amount, false);
        });

        return tl;
    }

    public hideOutro(amount:number):gsap.core.Timeline {
        const tl = gsap.timeline();
        tl.add([
            () => {this.outroSign.countUp.stopAt(amount, false)},
            gsap.to(this.background, {duration:0.5, alpha:0.0, ease:"none"}),
            gsap.to(this.outroSign, {duration:0.5, alpha:0.0, ease:"none"}),
            TheCage.reelView.show(0.5)
        ], "<");

        return tl;
    }

}